import { useEffect, useMemo, useState } from 'react';
import { Heart, Plus, Archive, Sparkles } from 'lucide-react';
import api from '../../../lib/api';
import { useAuth } from '../../../lib/AuthContext';
import { Dialog, DialogTrigger } from '../../ui/dialog';
import { QuoteSubmitDialog } from '../../QuoteWall';

const SEED = [
  { q: 'Every skill a child learns today opens a door they will walk through for the rest of their life.', a: '— Ludo ABA' },
  { q: 'Progress is measured in small steps. Celebrate every single one.', a: '— Ludo ABA' },
  { q: 'Consistency is the quiet superpower behind every breakthrough.', a: '— Anonymous BCBA' },
  { q: 'Meet the child where they are, then walk with them to where they can be.', a: '— Anonymous' },
  { q: 'Behind every new word, every new skill, is a team that never stopped believing.', a: '— Ludo ABA' },
  { q: 'Data tells us what is working. Heart tells us why it matters.', a: '— Anonymous' },
];

const greeting = () => {
  const h = Number(new Date().toLocaleString('en-US', { hour: '2-digit', hour12: false, timeZone: 'America/Chicago' }));
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
};

export const QuoteHero = () => {
  const { user } = useAuth();
  const [db, setDb] = useState([]);
  const [v, setV] = useState(0);
  const [open, setOpen] = useState(false);
  const load = () => api.get('/quotes/rotation?limit=200').then((r) => setDb(r.data?.quotes || [])).catch(() => setDb([]));
  useEffect(() => { load(); }, []);
  const pool = useMemo(() => [...db.map((q) => ({ q: q.text, a: `— ${q.author_name || 'Anonymous'}`, raw: q })), ...SEED], [db]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quote = useMemo(() => pool[Math.floor(Math.random() * pool.length)], [pool, v]);
  const heart = async () => {
    const r = await api.post(`/quotes/${quote.raw.quote_id}/heart`);
    setDb((qs) => qs.map((q) => (q.quote_id === r.data.quote_id ? r.data : q)));
  };
  const retire = async () => {
    if (!window.confirm('Retire this quote from the rotation?')) return;
    await api.patch(`/quotes/${quote.raw.quote_id}`, { status: 'retired' });
    await load(); setV((x) => x + 1);
  };
  const btn = 'inline-flex items-center gap-1 text-[11px] text-[#6E5F78] hover:text-[#2D2833] transition-colors';
  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#5E5068]/15 via-[#E9E5F0] to-[#F4F2F7] p-8 border border-[#E5E0EA] relative overflow-hidden ludo-rise" data-testid="ludo-welcome-card">
      <Sparkles size={64} className="absolute right-6 top-6 text-[#5E5068] opacity-15" />
      <div className="text-xs uppercase tracking-widest text-[#6E5F78] font-semibold">Good {greeting()}, {user?.name?.split(' ')[0]}</div>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2833] mt-2 leading-tight max-w-3xl">Small steps, big leaps — every session counts.</h1>
      <div className="mt-6 max-w-2xl">
        <div className="text-lg font-serif italic text-[#2D2833] leading-snug" data-testid="ludo-rotating-quote">"{quote.q}"</div>
        <div className="mt-2 flex items-center gap-4 flex-wrap">
          <span className="text-xs text-[#6E5F78]">{quote.a}</span>
          {quote.raw && (
            <button onClick={heart} className={`${btn} font-semibold ${quote.raw.viewer_has_hearted ? 'text-[#8A3A3A]' : ''}`} data-testid="ludo-quote-heart">
              <Heart size={12} fill={quote.raw.viewer_has_hearted ? '#8A3A3A' : 'none'} /> {quote.raw.heart_count || 0}
            </button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button className={btn} data-testid="ludo-quote-add-btn"><Plus size={11} /> Add quote</button></DialogTrigger>
            <QuoteSubmitDialog onDone={() => { setOpen(false); load(); setV((x) => x + 1); }} />
          </Dialog>
          {user?.role === 'super_user' && quote.raw && (
            <button onClick={retire} className={`${btn} text-[#8A3A3A]`} data-testid="ludo-quote-retire-btn"><Archive size={11} /> Retire</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteHero;
